import { FilterConfigRegistry } from '../../types/filter';
import { FilterType } from './FilterType';
import {
  adoptionRatingFilter,
  areaOfInterestFilter,
  classificationFilter,
  clinicalTrialFilter,
  comparisonPeriodFilter,
  countryFilter,
  dateRangeFilter,
  fairMarketValueRatingFilter,
  hcpOtherSpecialtyFilter,
  hcpProfessionFilter,
  hcpTypeOfPracticeFilter,
  hcpWithBlueskyFilter,
  indicationFilter,
  localSourceCountryFilter,
  ophtalmologyPersonaFilter,
  potentialRatingFilter,
  preferredChannelRatingFilter,
  productFilter,
  publicationFilter,
  rocheEmployeeFilter,
  rocheInteractionLadderFilter,
  secondarySpecialtyFilter,
  singleDateFilter,
  sourceSystemFilter,
  taeLevelFilter,
  taIndicationFilter,
  territoryFilter,
  therapeuticAreaFilter,
  topicFilter,
  trialSponsorFilter,
  valueAndMotivationFilter,
} from './configs';

// Import new scientific filters
import { congressFilter, guidelineFilter, socialMediaFilter } from './configs/scientific';

// Import new channel-specific filters
import {
  allInteractionTypeFilter,
  channelFilter,
  emailCampaignFilter,
  emailChannelFilter,
  emailTopicFilter,
  eventNameFilter,
  eventTypeFilter,
  fieldForceFilter,
  interactionIndicationFilter,
  interactionProductFilter,
  interactionTypeFilter,
  localProductFilter,
  oneToOneChannelsFilter,
  productMessageGroupFilter,
  productMessageNameFilter,
  promoFilter,
  websitePortalFilter,
} from './configs/channelSpecific';
import {
  competitorCompetitorFilter,
  competitorSurveyTitleFilter,
  competitorYearQuarterFilter,
} from './configs/competitorAnalysis';
import consentLevelFilter from './configs/consent/consentLevelFilter';
import consentPpocFilter from './configs/consent/consentPpocFilter';
import consentTierFilter from './configs/consent/consentTierFilter';
import consentTopicNameFilter from './configs/consent/consentTopicNameFilter';
import partnerScoreSurveyTitleFilter from './configs/partnerScore/partnerScoreSurveyTitleFilter';
import partnerScoreYearQuarterFilter from './configs/partnerScore/partnerScoreYearQuarterFilter';

// Import new CNET filters
import { npsFilter, trustAndReputation } from './configs/cnet';

import scientificOrganizationFilter from '@/models/filter/configs/scientific/scientificOrganizationFilter';
import hcpWithLinkedInFilter from './configs/general/hcpWithLinkedInFilter';
import stateProvinceFilter from './configs/general/stateProvinceFilter';
import xTwitterAccountFilter from './configs/general/xTwitterAccountFilter';

import hcoAccountPlanningCompletedFilter from './configs/hco/hcoAccountPlanningCompletedFilter';
import hcoAccountPlanningInProgressFilter from './configs/hco/hcoAccountPlanningInProgressFilter';
import hcoCountryFilter from './configs/hco/hcoCountryFilter';
import hcoPrivatePublicStatusFilter from './configs/hco/hcoPrivatePublicStatusFilter';
import hcoSourceSystemFilter from './configs/hco/hcoSourceSystemFilter';
import hcoSpecialtyFilter from './configs/hco/hcoSpecialtyFilter';
import hcoStateProvinceFilter from './configs/hco/hcoStateProvinceFilter';
import hcoTypeFilter from './configs/hco/hcoTypeFilter';

import {
  insightEmployeeNameFilter,
  insightEmployeeTypeFilter,
  insightFieldForceCodeFilter,
  insightIndicationFilter,
  insightProductFilter,
  insightTagFilter,
} from './configs/insights';
import insightAssignmentFilter from './configs/insights/insightAssignmentFilter';

import salesBrickFilter from './configs/sales/salesBrickFilter';
import salesMyTerritoryFilter from './configs/sales/salesMyTerritoryFilter';
import salesPrescriptionTypeFilter from './configs/sales/salesPrescriptionTypeFilter';
import salesProductGroupFilter from './configs/sales/salesProductGroupFilter';
import salesTerritoryFilter from './configs/sales/salesTerritoryFilter';

export const FILTER_CONFIG: FilterConfigRegistry = {
  [FilterType.SINGLE_DATE]: singleDateFilter,
  [FilterType.DATE_RANGE]: dateRangeFilter,
  [FilterType.COMPARISON_PERIOD]: comparisonPeriodFilter,
  [FilterType.COUNTRY]: countryFilter,
  [FilterType.TERRITORY]: territoryFilter,
  [FilterType.STATE_PROVINCE]: stateProvinceFilter,
  [FilterType.TA]: therapeuticAreaFilter,
  [FilterType.OTHER_SPECIALTY]: hcpOtherSpecialtyFilter,
  [FilterType.HCP_PROFESSION]: hcpProfessionFilter,
  [FilterType.HCP_TYPE_OF_PRACTICE]: hcpTypeOfPracticeFilter,
  [FilterType.TA_INDICATIONS]: taIndicationFilter,
  [FilterType.INDICATION]: indicationFilter,
  [FilterType.PRODUCTS]: productFilter,
  [FilterType.TAE_LEVELS]: taeLevelFilter,
  [FilterType.CLASSIFICATION_LEVELS]: classificationFilter,
  [FilterType.TOPIC]: topicFilter,
  [FilterType.CONGRESS]: congressFilter,
  [FilterType.PUBLICATION]: publicationFilter,
  [FilterType.CLINICAL_TRIAL]: clinicalTrialFilter,
  [FilterType.SOCIAL_MEDIA]: socialMediaFilter,
  [FilterType.GUIDELINE]: guidelineFilter,
  [FilterType.TRIAL_SPONSOR]: trialSponsorFilter,
  [FilterType.SCIENTIFIC_ORGANIZATION]: scientificOrganizationFilter,
  [FilterType.VALUE_AND_MOTIVATION]: valueAndMotivationFilter,
  [FilterType.FAIR_MARKET_VALUE_RATING]: fairMarketValueRatingFilter,
  [FilterType.ROCHE_INTERACTION_LADDER]: rocheInteractionLadderFilter,
  [FilterType.PREFERRED_CHANNEL_RATING]: preferredChannelRatingFilter,
  [FilterType.OPTHTALMOLOGY_PERSONAS]: ophtalmologyPersonaFilter,
  [FilterType.ADOPTION_RATING]: adoptionRatingFilter,
  [FilterType.POTENTIAL_RATING]: potentialRatingFilter,
  [FilterType.SOURCE_SYSTEM]: sourceSystemFilter,
  [FilterType.ONE_TO_ONE_CHANNELS]: oneToOneChannelsFilter,
  [FilterType.PRODUCT_MESSAGE_GROUP]: productMessageGroupFilter,
  [FilterType.PRODUCT_MESSAGE_NAME]: productMessageNameFilter,
  [FilterType.INTERACTION_TYPE]: interactionTypeFilter,
  [FilterType.EMAIL_CHANNEL]: emailChannelFilter,
  [FilterType.WEBSITE_PORTAL]: websitePortalFilter,
  [FilterType.PROMO]: promoFilter,
  [FilterType.EMAIL_CAMPAIGN]: emailCampaignFilter,
  [FilterType.EMAIL_TOPIC]: emailTopicFilter,
  [FilterType.EVENT_TYPE]: eventTypeFilter,
  [FilterType.EVENT_NAME]: eventNameFilter,
  [FilterType.FIELD_FORCE]: fieldForceFilter,
  [FilterType.ROCHE_EMPLOYEE]: rocheEmployeeFilter,
  [FilterType.INTERACTION_INDICATION]: interactionIndicationFilter,
  [FilterType.INTERACTION_PRODUCT]: interactionProductFilter,
  [FilterType.CHANNEL]: channelFilter,
  [FilterType.CONSENT_LEVEL]: consentLevelFilter,
  [FilterType.CONSENT_TOPIC_NAME]: consentTopicNameFilter,
  [FilterType.CONSENT_TIER]: consentTierFilter,
  [FilterType.CONSENT_PPOC]: consentPpocFilter,
  [FilterType.PARTNER_SCORE_YEAR_QUARTER]: partnerScoreYearQuarterFilter,
  [FilterType.PARTNER_SCORE_SURVEY_TITLE]: partnerScoreSurveyTitleFilter,
  [FilterType.COMPETITOR_YEAR_QUARTER]: competitorYearQuarterFilter,
  [FilterType.COMPETITOR_COMPETITOR]: competitorCompetitorFilter,
  [FilterType.COMPETITOR_SURVEY_TITLE]: competitorSurveyTitleFilter,
  [FilterType.DIRECT_MAIL]: consentLevelFilter,
  [FilterType.NPS_SCORE]: npsFilter,
  [FilterType.TRUST_REPUTATION]: trustAndReputation,
  [FilterType.INTERACTION_TYPE_ALL_CHANNELS]: allInteractionTypeFilter,
  [FilterType.PRODUCT_LOCAL]: localProductFilter,
  [FilterType.SECONDARY_SPECIALTY]: secondarySpecialtyFilter,
  [FilterType.AREA_OF_INTEREST]: areaOfInterestFilter,
  [FilterType.LOCAL_SOURCE]: localSourceCountryFilter,
  [FilterType.HCP_X_TWITTER_ACCOUNT]: xTwitterAccountFilter,
  [FilterType.HCP_WITH_LINKED_IN_ACCOUNT]: hcpWithLinkedInFilter,
  [FilterType.HCP_WITH_BLUESKY_ACCOUNT]: hcpWithBlueskyFilter,
  [FilterType.INSIGHT_INDICATION]: insightIndicationFilter,
  [FilterType.INSIGHT_PRODUCT]: insightProductFilter,
  [FilterType.INSIGHT_TAG]: insightTagFilter,
  [FilterType.INSIGHT_FIELD_FORCE_CODE]: insightFieldForceCodeFilter,
  [FilterType.INSIGHT_EMPLOYEE_TYPE]: insightEmployeeTypeFilter,
  [FilterType.INSIGHT_EMPLOYEE_NAME]: insightEmployeeNameFilter,
  [FilterType.INSIGHT_ASSIGNMENT]: insightAssignmentFilter,
  [FilterType.HCO_COUNTRY]: hcoCountryFilter,
  [FilterType.HCO_STATE_PROVINCE]: hcoStateProvinceFilter,
  [FilterType.HCO_TYPE]: hcoTypeFilter,
  [FilterType.HCO_SPECIALTY]: hcoSpecialtyFilter,
  [FilterType.HCO_PRIVATE_PUBLIC_STATUS]: hcoPrivatePublicStatusFilter,
  [FilterType.HCO_SOURCE_SYSTEM]: hcoSourceSystemFilter,
  [FilterType.HCO_ACCOUNT_PLANNING_IN_PROGRESS]: hcoAccountPlanningInProgressFilter,
  [FilterType.HCO_ACCOUNT_PLANNING_COMPLETED]: hcoAccountPlanningCompletedFilter,
  [FilterType.SALES_PRODUCT_GROUP]: salesProductGroupFilter,
  [FilterType.SALES_PRESCRIPTION_TYPE]: salesPrescriptionTypeFilter,
  [FilterType.SALES_TERRITORY]: salesTerritoryFilter,
  [FilterType.SALES_BRICK]: salesBrickFilter,
  [FilterType.SALES_MY_TERRITORY]: salesMyTerritoryFilter,
};

export const HCP_CHARACTERISTICS_FILTERS = {
  ['hcpCharacteristic.hcpXTwitterAccount']: xTwitterAccountFilter,
  ['hcpCharacteristic.hcpWithLinkedInAccount']: hcpWithLinkedInFilter,
  ['hcpCharacteristic.hcpWithBlueskyAccount']: hcpWithBlueskyFilter,
};
